import { AxiosResponse } from 'axios';
import * as ActionTypes from './actionTypes';

export interface ShowSessionWarningAction {
  type: typeof ActionTypes.SHOW_SESSION_WARNING
}
export const showSessionWarning = (): ShowSessionWarningAction => ({
  type: ActionTypes.SHOW_SESSION_WARNING,
});

export interface HideSessionWarningAction {
  type: typeof ActionTypes.HIDE_SESSION_WARNING
}
export const hideSessionWarning = (): HideSessionWarningAction => ({
  type: ActionTypes.HIDE_SESSION_WARNING,
});

export interface ManageSessionTimeoutAction {
  type: typeof ActionTypes.MANAGE_SESSION_TIMEOUT
}

export const manageSessionTimeout = (): ManageSessionTimeoutAction => ({
  type: ActionTypes.MANAGE_SESSION_TIMEOUT,
});

//
// ─── RENEW SESSION ──────────────────────────────────────────────────────────────
//

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: R E Q U E S T : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface RenewSessionRequestAction {
  type: typeof ActionTypes.FETCH_RENEW_SESSION_REQUEST
  payload: {
    token: string | null
  }
}
export const renewSessionRequest = (token: string | null): RenewSessionRequestAction => ({
  type: ActionTypes.FETCH_RENEW_SESSION_REQUEST,
  payload: {
    token,
  },
});

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: S U C C E S S : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface RenewSessionSuccessAction {
  type: typeof ActionTypes.FETCH_RENEW_SESSION_SUCCESS
  payload: Date
}
export const renewSessionSuccess = (lastSessionRenewed: Date): RenewSessionSuccessAction => ({
  type: ActionTypes.FETCH_RENEW_SESSION_SUCCESS,
  payload: lastSessionRenewed,
});

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: F A I L U R E : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface RenewSessionFailureAction {
  type: typeof ActionTypes.FETCH_RENEW_SESSION_FAILURE
}

export const renewSessionFailure = (): RenewSessionFailureAction => ({
  type: ActionTypes.FETCH_RENEW_SESSION_FAILURE,
});

//
// ─── LOGOUT ─────────────────────────────────────────────────────────────────────
//

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: R E Q U E S T : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface LogoutRequestAction {
  type: typeof ActionTypes.FETCH_LOGOUT_REQUEST
  payload: {
    timedOut: boolean
  }
}
export const logoutRequest = (timedOut = false): LogoutRequestAction => ({
  type: ActionTypes.FETCH_LOGOUT_REQUEST,
  payload: {
    timedOut,
  },
});

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: S U C C E S S : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface LogoutSuccessAction {
  type: typeof ActionTypes.FETCH_LOGOUT_SUCCESS
}

export const logoutSuccess = (): LogoutSuccessAction => ({
  type: ActionTypes.FETCH_LOGOUT_SUCCESS,
});

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: F A I L U R E : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface LogoutFailureAction {
  type: typeof ActionTypes.FETCH_LOGOUT_FAILURE
}

export const logoutFailure = (): LogoutFailureAction => ({
  type: ActionTypes.FETCH_LOGOUT_FAILURE,
});
//
// ────────────────────────────────────────────────── II ──────────
//   :::::: O T H E R : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────
//

export interface LoggedOffAction {
  type: typeof ActionTypes.LOGGED_OFF
}
/**
 * loggedOff is a side effect of api.fetch_logout_success and related methods.
 * This action is meant to act as a hook for other domains to function when the
 * user is successfully "logged off".
 */
export const loggedOff = (): LoggedOffAction => ({
  type: ActionTypes.LOGGED_OFF,
});

//
// ─── LOGIN ──────────────────────────────────────────────────────────────────────
//

export interface LoginResponse extends AxiosResponse {
  data: {
    token: string
    LastLogin: string
  }
}

/**
 * LoginFailureError is associated to the LoginFailureResponse, which is
 * returned from the server as a result of a failing login call.
 */
export interface LoginFailureError {
  message?: string
  code?: string
  status?: number
}
export interface LoginFailureResponse extends AxiosResponse {
  data: {
    errors: LoginFailureError[]
  }
}

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: R E Q U E S T : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface LoginRequestAction {
  type: typeof ActionTypes.FETCH_LOGIN_REQUEST
  payload: {
    username: string
    password: string
  }
}
export const loginRequest = (username: string, password: string): LoginRequestAction => ({
  type: ActionTypes.FETCH_LOGIN_REQUEST,
  payload: {
    username,
    password,
  },
});

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: S U C C E S S : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface LoginSuccessAction {
  type: typeof ActionTypes.FETCH_LOGIN_SUCCESS
  payload: {
    token: string
    lastLogin: string
    lastSessionRenewed: Date
  }
}
export const loginSuccess = (
  response: LoginResponse,
  lastSessionRenewed = new Date()
): LoginSuccessAction => ({
  type: ActionTypes.FETCH_LOGIN_SUCCESS,
  payload: {
    token: response.data.token,
    lastLogin: response.data.LastLogin,
    lastSessionRenewed,
  },
});

//
// ────────────────────────────────────────────────────── II ──────────
//   :::::: F A I L U R E : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────────
//

export interface LoginFailureAction {
  type: typeof ActionTypes.FETCH_LOGIN_FAILURE
  payload: {
    errors: LoginFailureError[]
    // thrownError: Error
  }
}
export const loginFailure = (errors: LoginFailureError[], thrownError?: Error) => ({
  type: ActionTypes.FETCH_LOGIN_FAILURE,
  payload: {
    errors,
    // thrownError
  },
});

//
// ────────────────────────────────────────────────── II ──────────
//   :::::: O T H E R : :  :   :    :     :        :          :
// ────────────────────────────────────────────────────────────
//

export interface LoggedInAction {
  type: typeof ActionTypes.LOGGED_IN
}
/**
 * loggedIn is a side effect of api.fetch_login_success and related methods.
 * This action is meant to act as a hook for other domains to function when the
 * user is successfully "logged in".
 */
export const loggedIn = () => ({
  type: ActionTypes.LOGGED_IN,
});

//
// ─── TYPES ──────────────────────────────────────────────────────────────────────
//

export type AuthenticationActions =
  | LogoutRequestAction
  | LogoutSuccessAction
  | LogoutFailureAction
  | LoginRequestAction
  | LoginSuccessAction
  | LoginFailureAction
  | RenewSessionRequestAction
  | RenewSessionSuccessAction
  | RenewSessionFailureAction
  | ShowSessionWarningAction
  | HideSessionWarningAction
  | LoggedInAction
  | LoggedOffAction
  | ManageSessionTimeoutAction
